import Card from 'react-bootstrap/Card';
import AppInput from '../atoms/AppInput';
import InfoHint from '../atoms/InfoHint';
import { IconTag } from '../atoms/Icon';
import { derivarMarca } from '../utils/color';

/** O roxo da plataforma. É o que o cardápio usa quando não há cor configurada —
 *  o padrão mora nos tokens CSS de lá, e este valor existe aqui só para o
 *  preview poder mostrar o que o lojista verá se deixar em branco. */
const PADRAO = '#534AB7';

const HEX_VALIDO = /^#[0-9a-fA-F]{6}$/;

export function buildBrand(establishment) {
  return { primaryColor: (establishment?.primaryColor || '').toUpperCase() };
}

export function sameBrand(a, b) {
  return a.primaryColor.trim().toUpperCase() === b.primaryColor.trim().toUpperCase();
}

/** O que impede de salvar, ou nulo.
 *
 *  Cor sem matiz NÃO entra aqui de propósito: cinza é um hex perfeitamente
 *  válido, o backend o aceita, e recusar o salvamento seria inventar uma regra
 *  que o contrato não tem. O que ela merece é aviso, não bloqueio — e o aviso
 *  está no corpo do formulário. */
export function brandError(brand) {
  const cor = brand.primaryColor.trim();
  if (!cor) return null;
  if (!HEX_VALIDO.test(cor)) return 'A cor da marca deve estar no formato #RRGGBB.';
  return null;
}

/** Campo vazio vai como string vazia, e não omitido: é assim que o backend
 *  entende "apague a cor e volte ao padrão". Omitir significa "não mandei", e o
 *  lojista que limpasse o campo veria a cor antiga voltar no próximo
 *  carregamento. Mesmo recurso da chave Pix.
 *
 *  Hex inválido não vira nem uma coisa nem outra: o campo sai do corpo. Antes
 *  ele caía no mesmo `''` do campo vazio, o que confundia "apaguei" com
 *  "digitei errado" — e apagar é destrutivo. Hoje `brandError` barra o
 *  salvamento antes de chegar aqui, então esse caminho não acontece; ele fica
 *  fechado para o dia em que alguém mexer no guard e transformar um hex pela
 *  metade em perda silenciosa da cor já salva. */
export function brandToPayload(brand) {
  const cor = brand.primaryColor.trim();
  if (!cor) return { primaryColor: '' };
  if (!HEX_VALIDO.test(cor)) return {};
  return { primaryColor: cor.toUpperCase() };
}

/** Um quadrado de cor com legenda. Usado aos pares — é a comparação lado a lado
 *  que impede o lojista de aprovar uma cor achando que verá outra. */
function Amostra({ cor, titulo, apoio }) {
  return (
    <div style={{ flex: '1 1 0', minWidth: 0 }}>
      <div
        style={{
          height: 56,
          borderRadius: 'var(--radius-md)',
          backgroundColor: cor,
          border: '1px solid rgba(0,0,0,0.08)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          // Texto branco dentro da própria amostra: é o teste que importa. O
          // lojista não precisa entender de contraste para ver que um rótulo
          // sumiu no fundo.
          color: '#fff',
          fontSize: 13,
          fontWeight: 600,
        }}
      >
        Fazer pedido
      </div>
      <div style={{ fontSize: 11.5, fontWeight: 600, color: '#555', marginTop: 6 }}>{titulo}</div>
      <div style={{ fontSize: 11, color: '#999' }}>{apoio}</div>
    </div>
  );
}

function BrandColorForm({ value, onChange }) {
  const cor = value.primaryColor.trim();
  const formatoOk = HEX_VALIDO.test(cor);
  const erro = brandError(value);

  // A mesma função que o cardápio usa, copiada com os vetores de teste junto:
  // um preview com matemática própria mentiria sobre contraste, e o lojista
  // aprovaria uma cor achando que está legível.
  const marca = formatoOk ? derivarMarca(cor) : null;

  // Hex válido que não vira paleta é cor sem matiz — preto, branco ou cinza. Os
  // cinco tons sairiam iguais e o cardápio ficaria sem cor de ação, então ele
  // cai no padrão. Precisa ser dito: sem isto o lojista escolhe cinza, salva com
  // sucesso, abre o cardápio e não entende por que nada mudou.
  const semMatiz = formatoOk && !marca;

  const exibida = marca ? marca.primary : PADRAO;

  // A frase é escolhida PELA direção que a derivação reporta, e nunca deduzida
  // aqui. Antes eu a inferia comparando contraste com o branco: funcionava, mas
  // fazia esta tela reconstruir a conclusão que a derivação acabou de tomar, e
  // reconstrução pode divergir da decisão sem nada acusar.
  //
  // O mapa é indexado em vez de ternário porque o conjunto de direções é do
  // `color.js`, não desta tela. Direção que ele venha a reportar e que não esteja
  // aqui cai no texto neutro — que é impreciso, mas verdadeiro. Um ternário
  // colocaria essa direção nova no ramo do `else` e faria a tela AFIRMAR a
  // direção errada, que é o defeito que este mapa existe para tornar impossível.
  //
  // `clareou` hoje não é produzido: a derivação parou de clarear cor escura,
  // porque preto é escolha legítima de marca — o que ela faz agora é inverter o
  // sentido do hover e do pressionado, sem mexer no tom. O ramo fica porque a
  // frase precisa continuar certa se esse comportamento voltar, e sair custaria
  // o mesmo defeito de novo.
  const AVISO_POR_DIRECAO = {
    escureceu: (
      <>
        Escurecemos sua cor para o texto branco continuar legível em cima dela. O cardápio
        vai usar <strong>{exibida}</strong>. Se a diferença incomodar, escolha um tom mais
        escuro — quanto mais clara a cor, mais ela precisa mudar.
      </>
    ),
    clareou: (
      <>
        Clareamos sua cor porque ela era escura demais para os estados do botão. O cardápio
        vai usar <strong>{exibida}</strong>.
      </>
    ),
  };

  const aviso = marca?.direcao ? AVISO_POR_DIRECAO[marca.direcao] : null;

  return (
    <Card className="border-0 card-elevated" style={{ borderRadius: 'var(--radius-md)' }}>
      <Card.Body className="p-4">
        <div className="d-flex align-items-center gap-2 mb-1">
          <IconTag size={16} style={{ color: 'var(--color-purple-primary)' }} />
          <h6 className="fw-semibold mb-0" style={{ color: 'var(--color-neutral)' }}>
            Cor da marca
          </h6>
        </div>
        <p className="mb-3" style={{ fontSize: 12, color: '#888' }}>
          Usada no cardápio que seus clientes abrem. Em branco, ele fica no roxo padrão.
        </p>

        <div className="d-flex gap-3 align-items-start flex-wrap">
          {/* Seletor nativo: no celular ele abre a roda de cores do sistema, que
              é melhor do que qualquer coisa que a gente desenhasse. O campo de
              texto ao lado existe para quem já tem o hex da marca anotado e não
              quer caçá-lo na roda. */}
          <input
            type="color"
            aria-label="Escolher a cor da marca"
            value={formatoOk ? cor : PADRAO}
            onChange={(e) => onChange({ primaryColor: e.target.value.toUpperCase() })}
            style={{
              width: 52,
              height: 52,
              padding: 2,
              borderRadius: 'var(--radius-md)',
              border: '1.5px solid #ddd',
              backgroundColor: '#fff',
              cursor: 'pointer',
              flexShrink: 0,
            }}
          />
          <div style={{ flex: '1 1 180px', minWidth: 160 }}>
            <AppInput
              label="Código da cor"
              value={value.primaryColor}
              onChange={(e) => onChange({ primaryColor: e.target.value.toUpperCase() })}
              placeholder={PADRAO}
              maxLength={7}
              autoComplete="off"
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck="false"
              error={erro || undefined}
              hint="Deixe em branco para usar o roxo padrão."
            />
          </div>
        </div>

        {/* As duas amostras só aparecem quando a cor precisou mudar. Mostrar
            "escolhida" e "no cardápio" iguais, lado a lado, faria o lojista
            procurar uma diferença que não existe. */}
        {marca && marca.ajustado ? (
          <>
            <div className="d-flex gap-3 mt-3">
              <Amostra cor={cor} titulo="Sua escolha" apoio={cor} />
              <Amostra cor={exibida} titulo="No cardápio" apoio={exibida} />
            </div>
            <InfoHint>
              {aviso || (
                // Direção que o `color.js` reporte e que esta tela não conheça.
                // Impreciso de propósito: não afirmar direção nenhuma é o único
                // texto que não pode estar errado.
                <>
                  Ajustamos sua cor para ela funcionar no cardápio, que vai
                  usar <strong>{exibida}</strong>.
                </>
              )}
            </InfoHint>
          </>
        ) : marca ? (
          <>
            <div className="d-flex gap-3 mt-3">
              <Amostra cor={exibida} titulo="No cardápio" apoio={exibida} />
              {/* O par vazio mantém a amostra na metade da largura, do mesmo
                  tamanho que ela tem no caso ajustado — sem ele o quadrado
                  estica e encolhe conforme o lojista digita. */}
              <div style={{ flex: '1 1 0' }} />
            </div>
            <InfoHint>Essa cor passa no contraste e será usada como você escolheu.</InfoHint>
          </>
        ) : null}

        {semMatiz && (
          <>
            <div className="d-flex gap-3 mt-3">
              <Amostra cor={cor} titulo="Sua escolha" apoio={cor} />
              <Amostra cor={PADRAO} titulo="No cardápio" apoio={PADRAO} />
            </div>
            <InfoHint>
              Preto, branco e tons de cinza não servem como cor de marca: o cardápio
              precisa de uma cor para destacar botões e preços, e sem matiz todos os
              tons sairiam iguais. Ele vai continuar no roxo padrão. Escolha uma cor
              com matiz para valer.
            </InfoHint>
          </>
        )}
      </Card.Body>
    </Card>
  );
}

export default BrandColorForm;
